import { HdGateGraphLayer, HdGateGraphLayout, HdGateGraphNode } from './hd-gate-graph.models';

/**
 * Layout-constanten van de radiale poort-visualisatie. Alle maten in eenheden
 * waarbij 1 eenheid = de straal van een basis-cirkel.
 *
 * De twee bovenste afstanden staan hier bewust niet in: `lineToColour` is
 * instelbaar (het bepaalt hoeveel lucht er rond een lijncirkel zit) en
 * `gateToLine` wordt daaruit afgeleid — zie {@link deriveGateToLine}.
 *
 * Zie `docs/superpowers/specs/2026-07-30-hd-gate-radial-graph-design.md` §3.
 */
export const HD_GATE_GRAPH_LAYOUT = {
  radius: { gate: 80, line: 30, colour: 7, tone: 3, base: 1.4 },
  /** Afstanden zijn genoemd naar het paar dat ze verbinden, niet naar één laag. */
  distance: { colourToTone: 13.4, toneToBase: 4.8 },
  /** Ruimte tussen de buitenring en de volle binnencirkel van laag 0 en 1. */
  ringPadding: 3.2,
  spread: { line: 360, deeper: 270 },
  gap: 0.6,
  margin: 3,
  /**
   * Instelbereik van `lineToColour`. Onder 45 kruipt een lijncirkel tegen de
   * deeltakken van zijn eigen kleur 1 en kleur 6 aan (die liggen door de
   * 270°-spreiding op ±135°, dus schuin achter hem); boven 70 wordt de hele
   * tekening zo groot dat de lijncirkels onleesbaar worden.
   */
  spacing: { min: 45, max: 70, default: 50 },
} as const;

/** Aantal kinderen per laag: 6 lijnen, 6 kleuren, 6 tonen, 5 basissen. */
const CHILD_COUNTS = [6, 6, 6, 5] as const;

/**
 * De 270°-spreiding leunt naar buiten, dus de conservatieve bovengrens uit de
 * driehoeksongelijkheid is ruim; hij mag 3% krapper. Empirisch gevalideerd over
 * het hele instelbereik — zie de bereik-test in de spec-file.
 */
const BRANCH_SEPARATION_FACTOR = 0.97;

/**
 * Afstand poort → lijn. Mag niet los ingesteld worden: wanneer de takken breder
 * worden moeten de lijnen verder uit elkaar, anders overlappen buurtakken.
 *
 * De formule is de bovengrens uit de driehoeksongelijkheid — een tak past altijd
 * binnen een cirkel met straal `branchRadius` rond zijn lijncirkel, dus twee
 * buurtakken raken elkaar niet zolang hun lijncirkels 2 × `branchRadius` uit
 * elkaar liggen.
 */
export function deriveGateToLine(lineToColour: number): number {
  const { distance, radius, gap } = HD_GATE_GRAPH_LAYOUT;
  const branchRadius = lineToColour + distance.colourToTone + distance.toneToBase + radius.base;
  return BRANCH_SEPARATION_FACTOR * (2 * branchRadius + gap);
}

/** Klemt een spreiding binnen het geldige bereik. */
export function clampHdGateGraphSpacing(spacing: number): number {
  const { min, max, default: fallback } = HD_GATE_GRAPH_LAYOUT.spacing;
  if (!Number.isFinite(spacing)) return fallback;
  return Math.min(max, Math.max(min, spacing));
}

/** Muteerbare variant tijdens de opbouw; naar buiten toe is alles readonly. */
interface MutableNode extends Omit<HdGateGraphNode, 'children' | 'parent'> {
  parent?: MutableNode;
  children: MutableNode[];
}

/**
 * Bouwt de volledige boom van 1339 cirkels: 1 poort, 6 lijnen, 36 kleuren,
 * 216 tonen en 1080 basissen.
 *
 * Spreidingsregels:
 *  - laag 1 verdeelt de zes lijnen over de volle 360°, startend bovenaan (−90°);
 *    er is geen connector naar een grootouder om te ontwijken;
 *  - lagen 2 t/m 4 verdelen hun kinderen over 270°, gecentreerd tegenover de
 *    connector naar de ouder, met de eindpunten op ±135°. De overige 90° rond
 *    die connector blijft vrij als ademruimte.
 *
 * Pure functie: geen Angular, geen DOM, deterministisch.
 */
export function buildHdGateGraphLayout(lineToColour: number): HdGateGraphLayout {
  const cfg = HD_GATE_GRAPH_LAYOUT;
  const spacing = clampHdGateGraphSpacing(lineToColour);
  const distances = [
    deriveGateToLine(spacing),
    spacing,
    cfg.distance.colourToTone,
    cfg.distance.toneToBase,
  ];
  const radii = [cfg.radius.line, cfg.radius.colour, cfg.radius.tone, cfg.radius.base];

  const root: MutableNode = {
    id: 'g',
    layer: 0,
    x: 0,
    y: 0,
    radius: cfg.radius.gate,
    direction: 0,
    children: [],
  };
  const nodes: MutableNode[] = [root];
  const queue: [MutableNode, number][] = [[root, 1]];

  while (queue.length > 0) {
    const [parent, layer] = queue.shift()!;
    if (layer > 4) continue;
    const count = CHILD_COUNTS[layer - 1];
    const distance = distances[layer - 1];
    const radius = radii[layer - 1];
    const spread = layer === 1 ? cfg.spread.line : cfg.spread.deeper;

    for (let index = 0; index < count; index++) {
      const direction = layer === 1
        ? -90 + index * (spread / count)
        : parent.direction - spread / 2 + index * (spread / (count - 1));
      const radians = (direction * Math.PI) / 180;
      const node: MutableNode = {
        id: `${parent.id}.${index + 1}`,
        layer: layer as HdGateGraphLayer,
        x: parent.x + distance * Math.cos(radians),
        y: parent.y + distance * Math.sin(radians),
        radius,
        direction,
        num: index + 1,
        parent,
        children: [],
      };
      parent.children.push(node);
      nodes.push(node);
      queue.push([node, layer + 1]);
    }
  }

  const extent = nodes.reduce(
    (max, node) => Math.max(max, Math.hypot(node.x, node.y) + node.radius),
    0,
  ) + cfg.margin;

  return { root, nodes, extent };
}

/**
 * Connector van rand tot rand in plaats van middelpunt tot middelpunt, zodat de
 * lijn niet dwars door de poort- of lijncirkel loopt.
 */
export function trimEdge(
  parent: Pick<HdGateGraphNode, 'x' | 'y' | 'radius'>,
  child: Pick<HdGateGraphNode, 'x' | 'y' | 'radius'>,
): { x1: number; y1: number; x2: number; y2: number } {
  const dx = child.x - parent.x;
  const dy = child.y - parent.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  return {
    x1: parent.x + ux * parent.radius,
    y1: parent.y + uy * parent.radius,
    x2: child.x - ux * child.radius,
    y2: child.y - uy * child.radius,
  };
}
